import { Database, Eye, Plug, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { McpInfo } from '../types';
import { CodeSnippet, CopyButton } from './copy-button';

interface StatProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function Stat({ icon, label, value }: StatProps) {
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

interface ConnectionCardProps {
  info: McpInfo | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** Блок «Подключение»: endpoint, тенант, число операций и готовые команды для клиентов. */
export function ConnectionCard({ info, isLoading, error }: ConnectionCardProps) {
  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />;
  }
  if (error || !info) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          Не удалось загрузить сведения о подключении{error ? `: ${error.message}` : ''}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-5 w-5" /> Подключение
        </CardTitle>
        <CardDescription>
          ИИ-клиент (Claude Code, Claude Desktop, Cursor) видит только операции, разрешённые вашей роли.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Адрес MCP-сервера</span>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border bg-muted/50 px-3 py-2 font-mono text-xs">
              {info.endpoint}
            </code>
            <CopyButton value={info.endpoint} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={<Database className="h-3.5 w-3.5" />} label="Площадка" value={info.tenant} />
          <Stat
            icon={<Wrench className="h-3.5 w-3.5" />}
            label="Доступно вашей роли"
            value={`${info.availableOperations} из ${info.totalOperations}`}
          />
          <Stat icon={<Eye className="h-3.5 w-3.5" />} label="Только чтение" value={info.availableReadOnly} />
          <Stat
            icon={<Wrench className="h-3.5 w-3.5" />}
            label="Изменяющие"
            value={info.availableOperations - info.availableReadOnly}
          />
        </div>

        {info.sections.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {info.sections.map((section) => (
              <Badge key={section.tag} variant="outline" className="font-normal">
                {section.tag} · {section.count}
              </Badge>
            ))}
          </div>
        ) : null}

        <CodeSnippet
          title="Claude Code — вход через браузер (без токена)"
          value={info.snippets.claudeCode}
          hint="При первом обращении откроется страница согласия в админке — токен создастся автоматически."
        />
        <CodeSnippet
          title="Claude Code — прямой режим (каждая операция отдельным инструментом)"
          value={info.snippets.claudeCodeDirect}
        />
        <CodeSnippet title="JSON-конфиг (Claude Desktop, Cursor и др.)" value={info.snippets.jsonConfig} />
      </CardContent>
    </Card>
  );
}
